/**
 * Display formatting helpers — ported from v56.2 lines 148-358.
 */

/**
 * Truncate address to 8 chars on each end with ellipsis.
 * Enough to visually verify against wallet, prevents lookalike attacks.
 */
export function shortAddr(addr: string | undefined | null): string {
  if (!addr) return ''
  return `${addr.slice(0, 8)}\u2026${addr.slice(-8)}`
}

/**
 * Truncate a transaction hash for display (6+6 with ellipsis).
 */
export function shortHash(hash: string | undefined | null): string {
  if (!hash) return ''
  if (hash.length <= 14) return hash
  return `${hash.slice(0, 6)}\u2026${hash.slice(-6)}`
}

export interface TokenLike {
  isNative?: boolean
  decimals?: number
}

/**
 * Format a token amount with appropriate decimal places.
 * Stablecoins: 2dp. Native tokens: 4dp (>= 1), 6dp (< 1).
 *
 * Truncates (floors) rather than rounding so the displayed balance never
 * exceeds the actual on-chain balance. Leaves dust in the wallet but
 * avoids misleading users into thinking they have more than they do.
 */
export function formatTokenAmount(amount: number | string | undefined | null, token: TokenLike): string {
  const safe = isFinite(Number(amount)) ? Number(amount) : 0
  const displayDecimals = token.isNative
    ? (safe >= 1 ? 4 : 6)
    : 2
  const factor = 10 ** displayDecimals
  const truncated = Math.floor(safe * factor) / factor
  return truncated.toLocaleString(undefined, {
    minimumFractionDigits: displayDecimals,
    maximumFractionDigits: displayDecimals,
  })
}

/**
 * Floor a value to `dp` decimals WITHOUT float error — so a computed SEND amount (Max, reverse-fee math)
 * NEVER rounds UP past the real balance and reverts with "insufficient balance" (#94 Bug 1). `Math.floor(
 * 2.92 * 100) / 100` gives 2.91 because `2.92 * 100 === 291.9999…`; this truncates the fixed-precision
 * STRING instead. A string input (e.g. a balance) is truncated exactly (no float round-trip at all);
 * a number is fixed to a few extra digits first to absorb its binary representation.
 */
export function floorToDecimals(value: number | string, dp: number): number {
  const s = typeof value === 'string' ? value.trim() : (Number.isFinite(value) ? value.toFixed(dp + 6) : '')
  if (!s || s === '-') return 0
  const neg = s.startsWith('-')
  const body = neg ? s.slice(1) : s
  const dot = body.indexOf('.')
  const truncated = dot < 0 ? body : body.slice(0, dp === 0 ? dot : dot + 1 + dp)
  const out = parseFloat((neg ? '-' : '') + truncated)
  return Number.isFinite(out) ? out : 0
}

/**
 * True when `hash` is a real, block-explorer-linkable tx hash — NOT a backend sentinel. A CCTP mint that
 * was already consumed on the destination is recorded as `mint_tx_hash = 'MINTED_EXTERNALLY'` (mint-service.ts):
 * the transfer is genuinely complete and the funds landed, but there is no OUR-side tx to link. Guarding on
 * this stops the UI rendering "TX: MINTED_EX…" with a broken explorer link on the delivered step.
 */
export function isExplorableTxHash(hash: string | undefined | null): hash is string {
  return !!hash && hash !== 'MINTED_EXTERNALLY'
}

export interface FiatCurrency {
  code: string
  symbol: string
  decimals: number
}

/**
 * Render a USD amount in any selected fiat currency.
 * Handles zero-decimal currencies (JPY, KRW, etc.) and thousands separators.
 *
 * @param usdAmount - The amount in USD
 * @param fiat - The target fiat currency metadata
 * @param rate - The USD → fiat conversion rate (default 1)
 */
export function formatFiat(usdAmount: number, fiat: FiatCurrency, rate = 1): string {
  const safe = isFinite(usdAmount) ? usdAmount : 0
  const converted = safe * rate
  const formatted = converted.toLocaleString(undefined, {
    minimumFractionDigits: fiat.decimals,
    maximumFractionDigits: fiat.decimals,
  })
  return `${fiat.symbol}${formatted}`
}

/**
 * Format a fee percentage for display.
 * Clamps to ">100%" when fees exceed the transfer amount.
 */
export function formatFeePct(pct: number, decimals = 1): string {
  if (!isFinite(pct) || pct < 0) return '0%'
  if (pct >= 100) return '>100%'
  return `${pct.toFixed(decimals)}%`
}
